import { useState, useRef, useEffect } from 'react'
import { Box, Tooltip, InputBase } from '@mui/material'
import { parseUnits, lotWarning } from './fourToTwelve'

const GREEN = '#15803D'
const AMBER = '#B45309'
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString())

// FAR-76 §4: a SKU-month's Units. Click (or Enter on focus) to edit; Enter or
// blur saves, Escape cancels. An edited cell has a green border and corner
// marker, and its tooltip gives the Base Plan value and the difference.
//
// Open Item 5 (Tyler, 2026-10-17): what the planner types is saved as typed.
// When it isn't a multiple of the item's lot size the cell is outlined amber
// and the tooltip says so; nothing is rounded.
export default function UnitsCell({ units, baseUnits, edited, review, roundingValue, disabled, onCommit, sx, label, lockedTip }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [invalid, setInvalid] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const start = () => {
    if (disabled) return
    setText(units == null ? '' : String(Math.round(units)))
    setInvalid(false)
    setEditing(true)
  }
  const commit = () => {
    const v = parseUnits(text)
    if (v == null) {
      setInvalid(true)
      return
    }
    setEditing(false)
    if (units == null || v !== Math.round(units)) onCommit(v)
  }

  const lot = edited ? lotWarning(Math.round(units ?? 0), roundingValue) : null
  const diff = baseUnits != null && units != null ? Math.round(units - baseUnits) : null
  const tip = edited ? (
    <Box sx={{ fontSize: '0.75rem' }}>
      <div>Edited · Base Plan {num(baseUnits)}</div>
      {diff != null && <div>Difference {diff > 0 ? '+' : ''}{diff.toLocaleString()}</div>}
      {lot && <div style={{ marginTop: 4 }}>Not a multiple of the lot size ({lot.toLocaleString()})</div>}
      {review && <div style={{ marginTop: 4, fontWeight: 600 }}>Needs review: {review} (typed on an earlier Base Plan)</div>}
      {lockedTip && <div style={{ marginTop: 4 }}>{lockedTip}</div>}
    </Box>
  ) : (lockedTip || '') // FAR-88: why an approved tab's box can't be edited

  // FAR-83 §4.3 / FAR-77 §5.4: UNITS is a white edit box inside the (possibly
  // tinted) cell, so editable cells stand out. Edit states mark the box.
  const box = {
    bgcolor: '#fff', border: '1px solid', borderColor: 'grey.300', borderRadius: 1,
    py: '2px', mx: 'auto', position: 'relative', lineHeight: 1.4,
    // FAR-88: a read-only box (an approved tab) isn't drawn as an edit box.
    ...(disabled && { bgcolor: 'transparent', borderColor: 'transparent' }),
  }
  const mark = (color) => ({
    borderColor: color, boxShadow: `inset 0 0 0 1px ${color}`,
    '&::after': {
      content: '""', position: 'absolute', top: 0, right: 0,
      borderTop: `7px solid ${color}`, borderLeft: '7px solid transparent',
    },
  })

  if (editing) {
    return (
      <Box component="td" sx={sx}>
        <Box sx={{ ...box, py: 0, borderColor: invalid ? '#DC2626' : GREEN, boxShadow: `inset 0 0 0 1px ${invalid ? '#DC2626' : GREEN}` }}>
        <InputBase
          inputRef={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setInvalid(false) }}
          onBlur={() => (parseUnits(text) == null ? setEditing(false) : commit())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            if (e.key === 'Escape') { e.preventDefault(); setEditing(false) }
          }}
          inputProps={{ 'aria-label': `${label} units`, inputMode: 'numeric', style: { textAlign: 'center', padding: '2px 0', fontSize: '0.8rem', fontWeight: 600 } }}
          sx={{ width: '100%' }}
        />
        </Box>
      </Box>
    )
  }

  return (
    <Tooltip title={tip} arrow disableInteractive>
      <Box
        component="td"
        tabIndex={disabled ? -1 : 0}
        role={disabled ? undefined : 'button'}
        aria-label={disabled ? undefined : `Edit ${label} units`}
        data-edited={edited ? 'true' : undefined}
        data-lot-warning={lot ? 'true' : undefined}
        data-needs-review={review ? 'true' : undefined}
        onClick={start}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); start() } }}
        sx={{
          ...sx,
          fontWeight: 600,
          cursor: disabled ? 'default' : 'text',
          // Hover darkens an unedited box only; an edited box keeps its green
          // (or amber) border.
          '&:hover > div': disabled || edited ? undefined : { borderColor: 'grey.500' },
        }}
      >
        <Box sx={{
          ...box,
          ...(edited && {
            ...(review && { bgcolor: '#FEF3C7' }), // FAR-79: amber fill = needs review
            ...mark(lot || review ? AMBER : GREEN), // green border + corner marker
          }),
        }}>
          {num(units)}
        </Box>
      </Box>
    </Tooltip>
  )
}
